import * as CANNON from 'cannon';
import { JointLimits } from './joint-limits.interface';
import {
    deg,
    BONE_JOINT,
    CONE_ANGLE,
    LEG_TWIST_ANGLE,
    CONSTRAINT_STIFFNESS,
    CONSTRAINT_RELAXATION,
} from './ragdoll-constants';

/**
 * Encapsulates all joint constraint definitions and provides factory methods
 * for creating and configuring CANNON.js constraints.
 *
 * Single Responsibility: only manages joint limits and constraint creation.
 */
export class JointConstraints {
    private static readonly LEG_JOINTS = new Set(['Hip', 'Knee', 'Ankle']);

    private static readonly DEFINITIONS: Readonly<Record<string, JointLimits>> = {
        Neck: {
            type: 'coneTwist',
            coneAngle: deg(20), twistAngle: deg(40), softLimitBuffer: deg(7), damping: 0.2,
            pitch: { min: -deg(60), max: deg(15) },
            yaw: { min: -deg(70), max: deg(10) },
            roll: { min: -deg(40), max: deg(10) },
        },
        Spine: {
            type: 'coneTwist',
            coneAngle: deg(30), twistAngle: deg(40), softLimitBuffer: deg(7), damping: 0.15,
            pitch: { min: -deg(30), max: deg(30) },
            yaw: { min: -deg(30), max: deg(30) },
            roll: { min: -deg(40), max: deg(40) },
        },
        Shoulder: {
            type: 'ballSocket',
            coneAngle: deg(25), twistAngle: deg(25), softLimitBuffer: deg(7), damping: 0.2,
            pitch: { min: -deg(30), max: deg(30) },
            yaw: { min: -deg(30), max: deg(30) },
            roll: { min: -deg(30), max: deg(30) },
        },
        UpperArm: {
            type: 'ballSocket',
            coneAngle: deg(60), twistAngle: deg(80), softLimitBuffer: deg(7), damping: 0.15,
            pitch: { min: -deg(90), max: deg(90) },
            yaw: { min: -deg(90), max: deg(90) },
            roll: { min: -deg(90), max: deg(90) },
        },
        Elbow: {
            type: 'hinge',
            coneAngle: deg(45), twistAngle: deg(90), softLimitBuffer: deg(7), damping: 0.15,
            pitch: { min: 0, max: deg(45) },
            yaw: { min: -deg(90), max: deg(90) },
            roll: { min: 0, max: 0 },
        },
        Wrist: {
            type: 'coneTwist',
            coneAngle: deg(30), twistAngle: deg(80), softLimitBuffer: deg(7), damping: 0.15,
            pitch: { min: -deg(80), max: deg(80) },
            yaw: { min: -deg(20), max: deg(30) },
            roll: { min: -deg(30), max: deg(30) },
        },
        Hip: {
            type: 'ballSocket',
            coneAngle: deg(15), twistAngle: deg(45), softLimitBuffer: deg(7), damping: 0.2,
            pitch: { min: -deg(30), max: deg(90) },
            yaw: { min: -deg(25), max: deg(45) },
            roll: { min: -deg(45), max: deg(45) },
        },
        Knee: {
            type: 'hinge',
            coneAngle: deg(1), twistAngle: deg(1), softLimitBuffer: deg(1), damping: 0.01,
            pitch: { min: 0, max: deg(0) },
            yaw: { min: -deg(1), max: deg(1) },
            roll: { min: 0, max: 0 },
        },
        Ankle: {
            type: 'coneTwist',
            coneAngle: deg(10), twistAngle: deg(10), softLimitBuffer: deg(7), damping: 0.2,
            pitch: { min: -deg(50), max: deg(20) },
            yaw: { min: -deg(20), max: deg(20) },
            roll: { min: -deg(20), max: deg(20) },
        },
    };

    /** Get the joint limits for a given bone name. */
    public getLimitsForBone(boneName: string): JointLimits | undefined {
        const jointName = BONE_JOINT[boneName];
        return jointName ? JointConstraints.DEFINITIONS[jointName] : undefined;
    }

    /** Compute the effective cone angle for a bone, accounting for soft limit buffer. */
    public getConeAngleForBone(boneName: string): number {
        const limits = this.getLimitsForBone(boneName);
        if (!limits) { return CONE_ANGLE; }
        return Math.max(limits.coneAngle - limits.softLimitBuffer, 0);
    }

    /** Clamp an angle to the allowed range for a bone on a given axis. */
    public clampAngle(boneName: string, axis: 'pitch' | 'yaw' | 'roll', angleRad: number): number {
        const limits = this.getLimitsForBone(boneName);
        const axisLimits = limits?.[axis];
        if (!axisLimits) { return angleRad; }
        return Math.max(axisLimits.min, Math.min(axisLimits.max, angleRad));
    }

    /** Create a ConeTwistConstraint between parent and child bodies. */
    public createConstraint(
        parentBody: CANNON.Body,
        childBody: CANNON.Body,
        pivotA: CANNON.Vec3,
        pivotB: CANNON.Vec3,
        axisA: CANNON.Vec3,
        axisB: CANNON.Vec3,
        boneName: string,
    ): CANNON.ConeTwistConstraint {
        const coneAngle = this.getConeAngleForBone(boneName);
        const limits = this.getLimitsForBone(boneName);
        const jointName = BONE_JOINT[boneName];
        const isLegJoint = JointConstraints.LEG_JOINTS.has(jointName);
        const twistAngle = (!isLegJoint && limits) ? limits.twistAngle : LEG_TWIST_ANGLE;
        const constraint = new CANNON.ConeTwistConstraint(parentBody, childBody, {
            pivotA,
            pivotB,
            axisA,
            axisB,
            angle: coneAngle,
            twistAngle,
        } as any);
        JointConstraints.softenConstraint(constraint);
        return constraint;
    }

    /** Apply softened stiffness/relaxation to all constraint equations. */
    private static softenConstraint(constraint: CANNON.Constraint): void {
        (constraint as any).equations.forEach((eq: any) => {
            eq.stiffness = CONSTRAINT_STIFFNESS;
            eq.relaxation = CONSTRAINT_RELAXATION;
        });
    }
}

